import { api } from '../config/api';
import { later, query, withEmbedMock } from './embed';
import * as mock from './billingMock';

// ---- Brand billing (Számlázás) ----
//
// The brand keeps a card on file; Outfino charges it monthly. Contract
// (Outfino/api feature/partner-billing):
//
//   GET    /brands/:id/billing
//     → { customer: { name, email, taxId,
//                     address: { line1, line2, city, postalCode, country } } | null,
//         paymentMethod: { brand, last4, expMonth, expYear } | null,
//         invoices: [{ id, number, amount, currency, status,
//                      periodStart, periodEnd, hostedUrl, pdfUrl }],
//         publishableKey,
//         cardStatus: 'valid'|'missing'|'expired'|'failed',
//         usage?: <the current month, as below> }
//   GET    /brands/:id/billing/status                 any member of the brand
//     → { cardStatus }   — for the card gate of members who cannot open the
//                          billing itself (403 there without manageBilling)
//   POST   /brands/:id/billing/setup-intent           → { clientSecret }
//   POST   /brands/:id/billing/payment-method         { paymentMethodId }
//   DELETE /brands/:id/billing/payment-method
//   PUT    /brands/:id/billing/details                { name, email, taxId, address }
//   GET    /brands/:id/billing/usage?period=YYYY-MM
//     → { period, currency, baseFee, trial, includedViews, uniqueViews,
//         overageViews, overageAmount, overageCap, matchedPurchases,
//         matchedRevenue, commissionRate, commissionAmount, estimatedTotal }
//
// Money (invoice `amount`, the usage amounts) is read in major units of
// `currency` (19990 = 19 990 Ft, 49 = 49 €), like the embed revenue figures.
//
// The card itself never touches our API: Stripe Elements collect it with the
// API's `publishableKey` and confirm the SetupIntent; we only send the
// resulting payment-method id.
//
// TEST DATA (api/billingMock.js) stands in like on the embed pages: outside
// production builds when the endpoint answers 404, or always with
// REACT_APP_EMBED_MOCK=1. The test data has no publishableKey, so real Stripe
// is never loaded for it; the page shows a test card form instead. The loaders
// resolve to { data, mock }, and the writes take that `mock` flag so a page
// on test data keeps writing to the test data.

/// Who may change the card and the details: the brand's owner, a member with
/// the `manageBilling` permission (a Tagok toggle, off by default; one of
/// BRAND_PERMISSIONS), or a platform admin. Everyone else on the brand sees
/// the page read-only.
export const BILLING_PERMISSION = 'manageBilling';

/// A platform admin: adminRole ADMIN or above (the API's ladder; `admin` is
/// the session's boolean for the same).
const ADMIN_RANKS = new Set(['ADMIN', 'SUPERADMIN', 'FOUNDER']);
export function isPlatformAdmin(user) {
  if (!user) return false;
  if (user.admin === true) return true;
  return ADMIN_RANKS.has(String(user.adminRole || user.admin_role || '').toUpperCase());
}

export function canManageBilling(brand, userId, isAdmin) {
  if (isAdmin) return true;
  const mine = (brand?.members || []).find((m) => (m.user?.id || m.userId || m.user_id) === userId);
  if (!mine) return false;
  if (mine.role === 'owner') return true;
  const perms = mine.permissions;
  if (Array.isArray(perms)) return perms.includes(BILLING_PERMISSION);
  return !!(perms && typeof perms === 'object' && perms[BILLING_PERMISSION]);
}

export function fetchBilling(brandId) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/billing`),
    later(() => mock.mockBilling(brandId))
  );
}

export function fetchBillingStatus(brandId) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/billing/status`),
    later(() => ({ cardStatus: mock.mockBilling(brandId).cardStatus }))
  );
}

export function fetchBillingUsage(brandId, period) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/billing/usage${query({ period })}`),
    later(() => mock.mockUsage(brandId, period))
  );
}

export function createSetupIntent(brandId, { mock: isMock } = {}) {
  if (isMock) return later(() => mock.mockSetupIntent(brandId))();
  return api(`/brands/${brandId}/billing/setup-intent`, { method: 'POST', body: {} });
}

export function savePaymentMethod(brandId, paymentMethodId, { mock: isMock, card } = {}) {
  if (isMock) return later(() => mock.mockSavePaymentMethod(brandId, paymentMethodId, card))();
  return api(`/brands/${brandId}/billing/payment-method`, { method: 'POST', body: { paymentMethodId } });
}

export function removePaymentMethod(brandId, { mock: isMock } = {}) {
  if (isMock) return later(() => mock.mockRemovePaymentMethod(brandId))();
  return api(`/brands/${brandId}/billing/payment-method`, { method: 'DELETE' });
}

export function saveBillingDetails(brandId, details, { mock: isMock } = {}) {
  if (isMock) return later(() => mock.mockSaveDetails(brandId, details))();
  return api(`/brands/${brandId}/billing/details`, { method: 'PUT', body: details });
}

export { PRICING, pricingFor, periodKey, recentPeriods } from './billingPricing';
